import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, TableSettingsToolbar } from '@app/components'
import { LntAlert } from '@app/components/base'
import { Show } from '@app/components/util'

import { getTaskResults } from '@app/api/routes/monitoringTasks'

import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { MONITORING_TASK_STATUS } from '@app/api/constants'

import { usePaginationQueryParams, useErrorRedirect, useTableSearch, useTableSetting } from '@app/hooks'

import { getColumns, adaptCompetitorsPricesTableData } from './lib'
import { useMonitoringIdColumn } from './hooks'
import { columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { ReactNode } from 'react'
import type { GetTaskResultsParams } from '@app/api/routes/monitoringTasks/types'
import type { CompetitorsPricesTabProps } from './types'

const columns = getColumns()

export const CompetitorsPricesTab: React.FC<CompetitorsPricesTabProps> = ({ taskId, status }) => {
  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const tasksItemParams = useMemo(
    () => ({ ...paginationParams, id: taskId }),
    [paginationParams, taskId]
  ) as GetTaskResultsParams

  const {
    data: competitorsPricesTableData,
    isLoading: isTasksItemDataLoading,
    isError: isErrorTasksItem
  } = useQuery({
    retry: false,
    queryKey: [MONITORINGS_TASKS_KEY.tasksResults, tasksItemParams],
    queryFn: async () => await getTaskResults(tasksItemParams),
    select: taskResultsData => adaptCompetitorsPricesTableData(taskResultsData, taskId)
  })

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  useMonitoringIdColumn(columns, taskId)

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.competitorsPricesTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredTasksItemDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: competitorsPricesTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar: ReactNode = (
    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  )

  useErrorRedirect(isErrorTasksItem)

  return (
    <ContentLayout>
      <Show condition={ status === MONITORING_TASK_STATUS.NEEDS_CORRECTION }>
        <LntAlert
          type='warning'
          message='Были выявлены аномальные мониторинги. Проверьте их корректность, при необходимости исправьте и завершите мониторинг.'
          showIcon
        />
      </Show>

      <ExtendedTable
        loading={ isTasksItemDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredTasksItemDataSource }
        toolbar={ toolbar }
        pagination={ {
          ...pagination,
          total: competitorsPricesTableData?.count,
          onChange: handlePaginationChange
        } }
      />
    </ContentLayout>
  )
}
